import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { execute } from '@/graphql/execute';
import { updateProjectLinkPasswordMutation } from '@/mutations/project.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface ProjectLinkPasswordDialogProps {
  linkId: string;
  linkName: string;
  hasPassword: boolean;
  projectId: string;
}

export function ProjectLinkPasswordDialog({ linkId, linkName, hasPassword, projectId }: ProjectLinkPasswordDialogProps) {
  const { t } = useTranslation('components');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: async (newPassword: string | null) => {
      return execute(updateProjectLinkPasswordMutation, { id: linkId, password: newPassword });
    },
    onSuccess: (_, newPassword) => {
      analytics.capture('Project Link Password Updated', { action: newPassword ? 'set' : 'removed' });
      queryClient.invalidateQueries({ queryKey: ['projectLinks', projectId] });
      queryClient.invalidateQueries({ queryKey: ['allProjectLinks'] });
      toast.success(newPassword ? t('linkPassword.passwordUpdated') : t('linkPassword.passwordRemoved'));
      dismissActiveDialog();
    },
    onError: (error: any) => {
      toast.error(error.message || t('linkPassword.failedUpdate'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    mutation.mutate(password);
  };

  const handleRemove = () => {
    mutation.mutate(null);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{hasPassword ? t('linkPassword.updatePassword') : t('linkPassword.setPassword')}</DialogTitle>
        <DialogDescription>
          {hasPassword
            ? t('linkPassword.updateDescription', { name: linkName })
            : t('linkPassword.setDescription', { name: linkName })}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Input
            type='password'
            placeholder={t('linkPassword.enterNewPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={mutation.isPending}
            autoFocus
          />
        </div>

        <DialogFooter>
          {hasPassword && (
            <Button
              type='button'
              variant='outline'
              className='text-destructive hover:text-destructive'
              disabled={mutation.isPending}
              onClick={handleRemove}>
              {mutation.isPending ? <Loader2 className='size-4 animate-spin' /> : t('linkPassword.removePassword')}
            </Button>
          )}
          <Button type='button' variant='outline' onClick={dismissActiveDialog} disabled={mutation.isPending}>
            {t('linkPassword.cancel')}
          </Button>
          <Button type='submit' disabled={mutation.isPending || !password}>
            {mutation.isPending ? <Loader2 className='size-4 animate-spin' /> : hasPassword ? t('linkPassword.update') : t('linkPassword.setPassword')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
